import React, { useContext, useMemo, useRef } from 'react';
import {
  Flex,
  Carousel,
  RemoteImage,
  useScreenSize,
  IconFullscreen,
  IconArrowLeft,
  ColorValues,
  IconArrowRight,
  type SliderRef,
} from '@banhala/ui-kit';
import Animated, {
  interpolate,
  useAnimatedStyle,
  useSharedValue,
} from 'react-native-reanimated';
import { ScrollContext } from '../../context/ScrollContext';
import { RelatedGoodsView } from './components/RelatedGoodsView';
import { BlurBackground } from './components/BlurBackground';
import { isNotNil, isWeb } from 'shared/src/utils';
import { useGoodsBasic } from '../../queries/useGoodsBasic';
import { usePreRenderParams } from '../../hooks/usePreRenderParams';
import { DetailedImageViewer } from './components/DetailedImageViewer';
import { AiStudioRedirect } from './components/AiStudioRedirect';
import { PaginationDots } from './components/PaginationDots';

type CarouselItem =
  | {
      type: 'image';
      uri: string;
    }
  | {
      type: 'related';
    };

export const CoverImages: React.FC = () => {
  const { width } = useScreenSize();
  const sliderRef = useRef<SliderRef | null>(null);

  const { scrollY } = useContext(ScrollContext);
  const [currentIndex, setCurrentIndex] = React.useState(0);
  const { data: goodsBasic, isSuccess: successBasicData } = useGoodsBasic();
  const { data: goodsBasicSkeleton } = usePreRenderParams();
  const isScrollNow = useRef<boolean>(false);

  const containerStyle = useAnimatedStyle(() => {
    if (!scrollY) return {};

    return {
      position: 'relative',
      height: interpolate(
        scrollY.value,
        [-width, 0],
        [width * 2, width],
        'clamp',
      ),
      width: width,
      transform: [
        {
          translateY: interpolate(
            scrollY.value,
            [-width, 0],
            [-width, 0],
            'clamp',
          ),
        },
      ],
      overflow: 'hidden',
    };
  });
  const [isVisible, setIsVisible] = React.useState(false);
  // const scrollValue = useSharedValue(0);

  const imageStyle = useAnimatedStyle(() => {
    if (!scrollY) return {};

    return {
      overflow: 'hidden',
      transform: [
        {
          scale: interpolate(scrollY.value, [-width * 0.5, 0], [2, 1], 'clamp'),
        },
      ],
    };
  });
  const carouselList = useMemo(() => {
    const images =
      isNotNil(goodsBasic) && isNotNil(goodsBasic.cover_images)
        ? goodsBasic?.cover_images
        : (goodsBasicSkeleton?.cover_images ?? []);

    return !successBasicData
      ? images.map(item => ({
          type: 'image' as const,
          uri: item,
        }))
      : [
          ...images.map(item => ({
            type: 'image' as const,
            uri: item,
          })),
          { type: 'related' as const },
        ];
  }, [goodsBasic, goodsBasicSkeleton?.cover_images, successBasicData]);

  const lastImage = useMemo(() => {
    if (!goodsBasic) return null;

    if (goodsBasic.cover_images.length > 0) {
      return goodsBasic.cover_images[goodsBasic.cover_images.length - 1];
    }
    return null;
  }, [goodsBasic]);

  const isShowArrow = useMemo(() => isWeb, []);

  const scrollX = useSharedValue(0);

  return (
    <Animated.View style={containerStyle}>
      <Carousel<CarouselItem>
        sliderRef={sliderRef}
        data={carouselList}
        onSnapToItem={setCurrentIndex}
        windowSize={2}
        renderItem={({ item, index }) => {
          if (item.type === 'image') {
            return (
              <Animated.View style={currentIndex === index ? imageStyle : {}}>
                <Flex
                  width={width}
                  height={width}
                  testID={`상품대표이미지-${index}`}
                  onPress={() => {
                    if (isScrollNow.current) return;
                    setIsVisible(true);
                  }}
                >
                  <RemoteImage uri={item.uri} width={width} height={width} />
                </Flex>
              </Animated.View>
            );
          }

          return (
            <Flex testID={`상품대표이미지-추천상품`}>
              {isNotNil(lastImage) && (
                <Animated.View style={currentIndex === index ? imageStyle : {}}>
                  <RemoteImage
                    uri={lastImage}
                    width={width}
                    height={width}
                    blurRadius={10}
                  />
                  <BlurBackground />
                </Animated.View>
              )}
              <RelatedGoodsView focused={currentIndex === index} />
            </Flex>
          );
        }}
        loop={false}
        vertical={false}
        width={width}
        height={isWeb ? width : undefined}
        onScrollStart={() => {
          isScrollNow.current = true;
        }}
        onScrollEnd={index => {
          isScrollNow.current = false;
          console.log('onScrollEnd', index);
          // setCurrentIndex(index);
        }}
        onProgressChange={(_, absoluteProgress) => {
          scrollX.value = absoluteProgress;
        }}
      />
      {carouselList[currentIndex]?.type !== 'related' && (
        <Flex
          padding={{ horizontal: 16, vertical: 16 }}
          position={'absolute'}
          right={0}
          bottom={0}
          onPress={() => setIsVisible(true)}
        >
          <IconFullscreen fill={'white'} />
        </Flex>
      )}
      {currentIndex === 0 && (
        <Flex
          padding={{ horizontal: 16, vertical: 16 }}
          position={'absolute'}
          left={0}
          bottom={0}
        >
          <AiStudioRedirect />
        </Flex>
      )}

      {isShowArrow && currentIndex > 0 && (
        <Flex.Center top={0} bottom={0} left={0} position={'absolute'}>
          <Flex.Center
            width={40}
            height={40}
            backgroundColor={'bg_black_button'}
            backgroundColorOpacity={0.4}
            onPress={() => sliderRef.current?.prev()}
          >
            <IconArrowLeft size={24} fill={ColorValues.gray50} />
          </Flex.Center>
        </Flex.Center>
      )}

      {isShowArrow && currentIndex < carouselList.length - 1 && (
        <Flex.Center top={0} bottom={0} right={0} position={'absolute'}>
          <Flex.Center
            width={40}
            height={40}
            backgroundColor={'bg_black_button'}
            backgroundColorOpacity={0.4}
            onPress={() => sliderRef.current?.next()}
          >
            <IconArrowRight size={24} fill={ColorValues.gray50} />
          </Flex.Center>
        </Flex.Center>
      )}

      <Flex.Center position={'absolute'} left={0} right={0} bottom={16}>
        <PaginationDots
          value={currentIndex}
          scrollX={scrollX}
          maxCount={(goodsBasic?.cover_images ?? [0, 1]).length + 1}
        />
      </Flex.Center>

      {isNotNil(goodsBasic) && isNotNil(goodsBasic.cover_images) && (
        <DetailedImageViewer
          visible={isVisible}
          images={goodsBasic.cover_images}
          onCloseViewer={() => setIsVisible(false)}
          currentIndex={Math.min(
            currentIndex,
            goodsBasic.cover_images.length - 1,
          )}
        />
      )}
    </Animated.View>
  );
};
